import type {ServiceWorkerAPI} from "../ServiceWorker/api";
import type {OffscreenAPI} from "../Pages/offscreen/api";
import type { InstrumentationEventDomEventFired } from "../lib/Events/InstrumentationEvent";
import { InstrumentationEvent } from "../lib/Events/InstrumentationEvent";
import { DataGoesWhereSettingsPartial } from "../lib/MultiWorld/DataGoesWhereSettings";
import { DbRecordedEvent, UUID } from "../State/EvidenceDB/DatabaseTypes";


export const CustomEventName = {
  ForwardMessageFromMainWorldToServiceWorker: "ForwardMessageFromMainWorldToServiceWorker",
  ForwardMessageFromServiceWorkerToMainWorld: "ForwardMessageFromServiceWorkerToMainWorld",
  MainWorldRequestsExtensionSettingsFromIsolatedWorld: "MainWorldRequestsSettingsFromIsolatedWorld",
  IsolatedWorldSendsExtensionSettingsToMainWorld: "IsolatedWorldSendsSettingsToMainWorld",
} as const;
// export const CustomEventName_ForwardMessageFromMainWorldToIsolatedRuntime = "ForwardMessageFromMainWorldToIsolatedRuntime";
// export const CustomEventName_ForwardMessageFromIsolatedRuntimeToMainWorld = "ForwardMessageFromIsolatedRuntimeToMainWorld";

export interface MessageBase<T extends string> {
  messageType: T;
}

export const isMessageBase = <T extends string>(names: readonly T[], message: unknown): message is MessageBase<T> =>
  message != null && typeof message === "object" &&
        ("messageType" in message) && (typeof message.messageType === "string") &&
        ((names as readonly string[]).includes(message["messageType"]))
  
export const AnnouncementNewEventAddedToDatabaseMessageName = "announcingNewEventsAddedInDatabase";
export interface AnnouncementEventAddedOrUpdatedInDatabase {
  recordingUuid: UUID;
  eventIndex: number;
  category: DbRecordedEvent["category"];
  eventName: DbRecordedEvent["eventName"];
}

export interface AnnouncementNewEventAddedToDatabase extends MessageBase<typeof AnnouncementNewEventAddedToDatabaseMessageName>, AnnouncementEventAddedOrUpdatedInDatabase {}

export const AnnouncementEventUpdatedInDatabaseMessageName = "announcingEventUpdatedInDatabase";
export interface AnnouncementEventUpdatedInDatabase extends MessageBase<typeof AnnouncementEventUpdatedInDatabaseMessageName>, AnnouncementEventAddedOrUpdatedInDatabase {}

export const AnnouncementRecordingCreatedMessageName = "announcingRecordingCreated";
export interface AnnouncementRecordingCreated extends MessageBase<typeof AnnouncementRecordingCreatedMessageName> {
  recordingUuid: UUID;
}
export const AnnouncementRecordingUpdatedMessageName = "announcingRecordingUpdated";
export interface AnnouncementRecordingUpdated extends MessageBase<typeof AnnouncementRecordingUpdatedMessageName> {
  recordingUuid: UUID;
}
export const AnnouncementRecordingDeletedMessageName = "announcingRecordingDeleted";
export interface AnnouncementRecordingDeleted extends MessageBase<typeof AnnouncementRecordingDeletedMessageName> {
  recordingUuid: UUID;
}


export const AnnouncementExtensionSettingsUpdatedMessageName = "announcingExtensionSettingsUpdated";
export interface AnnouncementExtensionSettingsUpdated extends MessageBase<typeof AnnouncementExtensionSettingsUpdatedMessageName> {
  // recordingUuid: UUID;
}

const RecordingAnnouncementMessageNames = [
  AnnouncementRecordingCreatedMessageName,
  AnnouncementRecordingUpdatedMessageName,
  AnnouncementRecordingDeletedMessageName,
 ] as const;
 export type RecordingAnnouncementMessageName = typeof RecordingAnnouncementMessageNames[number];

 const EventAnnouncementMessageNames = [
  AnnouncementNewEventAddedToDatabaseMessageName,
  AnnouncementEventUpdatedInDatabaseMessageName,
 ] as const;
 export type EventAnnouncementMessageName = typeof EventAnnouncementMessageNames[number];

 const SettingsAnnouncementMessageNames = [
  AnnouncementExtensionSettingsUpdatedMessageName,
 ] as const;

const AnnouncementMessageNames = [
  ...RecordingAnnouncementMessageNames,
  ...EventAnnouncementMessageNames,
  ...SettingsAnnouncementMessageNames,
 ] as const;
  export type AnnouncementMessageName = typeof AnnouncementMessageNames[number];

export type AnnouncementMessage = never |
  AnnouncementExtensionSettingsUpdated |
  AnnouncementNewEventAddedToDatabase | 
  AnnouncementEventUpdatedInDatabase |
  AnnouncementRecordingCreated |
  AnnouncementRecordingUpdated |
  AnnouncementRecordingDeleted |
  never;
  

export const MainWorldApiMessageName = {
  updateSettings: "updateSettings",
} as const;

export interface MessageToMainWorldBase<T extends string> extends MessageBase<T> {}

export interface MessageToMainWorldUpdateSettings extends MessageToMainWorldBase<typeof MainWorldApiMessageName.updateSettings> {
  messageType: typeof MainWorldApiMessageName.updateSettings;
  settings: DataGoesWhereSettingsPartial;
  tabId: number;
  isRecording: boolean;
}

export type MessageToMainWorld = MessageToMainWorldUpdateSettings;

export const SharedAPICommandName = {
  recordInstrumentationEvent: "recordInstrumentationEvent",
} as const;

export const ServiceWorkerAPICommandName = {
  ...SharedAPICommandName,
  getTabRecordingState: "getTabRecordingState",
  downloadEvidenceArchive: "downloadEvidenceArchive",
  startRecording: "startRecording",
  startCrawl: "startCrawl",
  setCrawlState: "setCrawlState",
  getCrawlState: "getCrawlState",
  addTabToRecording: "addTabToRecording",
  stopRecording: "stopRecording",
  resumeRecording: "resumeRecording",
} as const;

export const OffscreenAPICommandName = {
  startScreenRecording: "startScreenRecording",
  stopScreenRecording: "stopScreenRecording",
  pauseScreenRecording: "pauseScreenRecording",
  resumeScreenRecording: "resumeScreenRecording",
  flushScreenRecording: "flushScreenRecording",
} as const;
export type OffscreenAPICommandName = typeof OffscreenAPICommandName[keyof typeof OffscreenAPICommandName];
// export const APICommandName = {
//   ...SharedAPICommandName,
//   ...ServiceWorkerAPICommandName,
//   ...OffscreenAPICommandName,
// }
// export type ApiCommandName = typeof APICommandName;


export const ContentSourceSearchWorkerResultsKey = "ContentSourceSearchWorkerResults";

/**
 * Shared API messages
 */
export interface InstrumentationEventMessage {
  messageType: typeof SharedAPICommandName.recordInstrumentationEvent;
  instrumentationEvent: InstrumentationEvent;
}

/**
 * Service Worker API messages
 */
// export type GetSearchReportApiRequestMessage = Parameters<typeof ServiceWorkerAPI.getSearchReport>[0] & {
//   messageType: typeof ServiceWorkerAPICommandName.getSearchReport;
// }
// export type OpenSearchReportApiRequestMessage = Parameters<typeof ServiceWorkerAPI.openSearchReport>[0] & {
//   messageType: typeof ServiceWorkerAPICommandName.openSearchReport;
// }
// export type GetEvidenceArchiveApiRequestMessage = Parameters<typeof ServiceWorkerAPI.getEvidenceArchive>[0] & {
//   messageType: typeof ServiceWorkerAPICommandName.getEvidenceArchive;
// }
export type GetMyTabIdApiRequestMessage = {
  messageType: typeof ServiceWorkerAPICommandName.getTabRecordingState;
}
export type DownloadEvidenceArchiveApiRequestMessage = Parameters<typeof ServiceWorkerAPI.downloadEvidenceArchive>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.downloadEvidenceArchive;
}
export type StartCrawlApiRequestMessage = Parameters<typeof ServiceWorkerAPI.startCrawl>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.startCrawl;
}
export type SetCrawlStateApiRequestMessage = Parameters<typeof ServiceWorkerAPI.setCrawlState>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.setCrawlState;
}
export type getCrawlStateApiRequestMessage = Parameters<typeof ServiceWorkerAPI.getCrawlState>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.getCrawlState;
}
export type StartRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.startNewRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.startRecording;
}
export type AddTabToRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.addTabToRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.addTabToRecording;
}
export type ResumeRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.resumeRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.resumeRecording;
}
export type StopRecordingApiRequestMessage = Parameters<typeof ServiceWorkerAPI.stopRecording>[0] & {
  messageType: typeof ServiceWorkerAPICommandName.stopRecording;
}

/**
 * Offscreen API messages
 */
export type StartScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.startScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.startScreenRecording;
}
// export type StartRecordingScreenApiRequestMessage = Parameters<typeof OffscreenAPI.startRecordingStream>[0] & {
//   messageType: typeof OffscreenAPICommandName.startRecording;
// }
export type StopScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.stopScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.stopScreenRecording;
}
export type PauseScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.pauseScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.pauseScreenRecording;
}
export type ResumeScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.stopScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.resumeScreenRecording;
}
export type FlushScreenRecordingOffscreenApiRequestMessage = Parameters<typeof OffscreenAPI.flushScreenRecording>[0] & {
  messageType: typeof OffscreenAPICommandName.flushScreenRecording;
}


export type ServiceWorkerAPIRequestMessage =
  InstrumentationEventMessage |
  // GetSearchReportApiRequestMessage |
  // OpenSearchReportApiRequestMessage |
  GetMyTabIdApiRequestMessage |
  AddTabToRecordingApiRequestMessage |
  StartCrawlApiRequestMessage |
  SetCrawlStateApiRequestMessage |
  getCrawlStateApiRequestMessage |
  DownloadEvidenceArchiveApiRequestMessage |
  StartRecordingApiRequestMessage |
  ResumeRecordingApiRequestMessage |
  StopRecordingApiRequestMessage |
  never;


export type OffscreenAPIRequestMessage =
  StartScreenRecordingOffscreenApiRequestMessage |
  StopScreenRecordingOffscreenApiRequestMessage |
  PauseScreenRecordingOffscreenApiRequestMessage |
  ResumeScreenRecordingOffscreenApiRequestMessage |
  FlushScreenRecordingOffscreenApiRequestMessage |
  never;

export type ApiRequestMessage =
  ServiceWorkerAPIRequestMessage |
  InstrumentationEventMessage |
  never;

export type Message = ApiRequestMessage | AnnouncementMessage;

export const isMessage = <T extends string>(names: readonly T[]) => (message: unknown): message is MessageBase<T> & Message =>
  isMessageBase(names, message);
  
export const isOffscreenAPIRequestMessage = isMessage(Object.values(OffscreenAPICommandName));

export const isEventAnnouncementMessage = isMessage(EventAnnouncementMessageNames);
export const isRecordingAnnouncementMessage = isMessage(RecordingAnnouncementMessageNames);

export const isAnnouncementMessage = isMessage(AnnouncementMessageNames);

export const isServiceWorkerAPIRequestMessage = isMessage(Object.values(ServiceWorkerAPICommandName));

export const isAnnouncementSettingsUpdated = isMessage([AnnouncementExtensionSettingsUpdatedMessageName]);